import React, { Fragment, useEffect } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { Outlet } from 'react-router-dom';
import { useGlobalMetadata } from '../utils/providers';
import { useSidePanel } from './SidePanel/SidePanelContext';
import Sidebar from './Sidebar';
import Header from './Header';
import SidePanelShell from './SidePanel/SidePanelShell';
import RecordTargetResolver from './RecordTargetResolver';
import './SidebarLayout.css';

/**
 * SidebarLayout — full-height sidebar + main content area + persistent side panel.
 * The side panel lives here (above route level) so it persists across table navigation.
 */
function SidebarLayout(): React.ReactElement {
  const { data: metadata } = useGlobalMetadata();
  const { visible, openRecords, showPanel, togglePanel } = useSidePanel();
  const hasHiddenTabs = !visible && openRecords.length > 0;

  // Keyboard shortcut: Ctrl+\ (or Cmd+\) to toggle panel
  useEffect(() => {
    if (openRecords.length === 0) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '\\') {
        e.preventDefault();
        togglePanel();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [openRecords.length, togglePanel]);

  if (!metadata) {
    return <Fragment />;
  }

  return (
    <div className="sky-app" data-testid="app-shell">
      <RecordTargetResolver />
      <Sidebar />
      <main className="sky-main">
        <Header />
        <div className="sky-content-row">
          <div className="sky-content">
            <Outlet />
            <PageReadiness />
          </div>
          <SidePanelShell />
          {hasHiddenTabs && (
            <button
              className="sky-panel-toggle"
              onClick={showPanel}
              title={`Show panel (${openRecords.length} tab${openRecords.length > 1 ? 's' : ''}) · Ctrl+\\`}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span className="sky-panel-toggle-badge">{openRecords.length}</span>
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

// Subscribe only this marker, not the application layout, to request activity.
function PageReadiness(): React.ReactElement {
  const fetching = useIsFetching();
  return <span hidden data-testid="page-ready" data-ready={fetching === 0 ? 'true' : 'false'} />;
}

export default SidebarLayout;
